<template>
  <div class="columns cell small-4 cards-columns-container">
    <div class="card child_region">
      <div class="promotion">
        <div class="promo-header">
          <h2 class="promo-header-text" :class="{ LoyallistHeading: promotion.fobName === 'Loyallist' }">
            {{ promotion.promotionHeading }}
          </h2>
          <h2 class="promo-sub-header-text">
            {{ promotion.promotionSubHeading }}
          </h2>
        </div>
        <div class="promo-body">
          <div v-if="envProps?.entryPoint === 'bcom' && promotion.displayOfferExpiryDate" class="promo-exp-date">
            <p class="promo-exp-text">Ends On {{ promotion.expirationDate }}</p>
          </div>
          <div v-if="promotion.promotionSubDescription" class="promo-legal promo-desc">
            <p>{{ promotion.promotionSubDescription }}{{ promotion.promotionSubDescription2 }}</p>
          </div>
          <div v-if="envProps?.entryPoint === 'mcom'" class="promo-info">
            <p v-if="promotion.promotionCode" class="promo-code">Promo Code: {{ promotion.promotionCode }}</p>
            <p v-else>No Promo Code</p>
            <p v-if="promotion.displayOfferExpiryDate" class="promo-exp">Ends: {{ promotion.expirationDate }}</p>
          </div>
        </div>
        <div class="promo-footer">
          <a
            id="shop-now-link"
            :href="promotion.promotionClickThoughUrl"
            target="_blank"
            class="shop-link button expanded"
            aria-label="Shop Now Applies to select merchandise."
            >Shop Now</a
          >
          <div v-if="promotion.isWalletEligible && promotion.promotionCode && !promotion.global">
            <div v-if="!isUserLoggedIn">
              <a class="add-to-button button m-hide" title="Add To Wallet" href="/account/signin"> Add To Wallet </a>
            </div>
            <div v-else>
              <span>
                <button
                  :class="['add-to-button button', checkWalletPromocode() ? 'disable-add-wallet' : '']"
                  title="Add To Wallet"
                  @click="addPromoCode()"
                >
                  {{ checkWalletPromocode() ? 'Added To Wallet' : 'Add To Wallet' }}
                </button>
              </span>
            </div>
          </div>
          <template v-if="promotion.promotionLegalDisclaimer">
            <a v-if="envProps?.entryPoint === 'bcom'" href="#" class="info-link" @click.stop="openBcomExclusionsDetails"> Info/Exclusions </a>
            <a
              v-else
              href="javascript:void(0)"
              class="info-link InfoExclusionLink"
              title="Offer Details &amp; Exclusions"
              @click.stop="openExclusionsDetails()"
            >
              Exclusions &amp; Details
            </a>
          </template>
        </div>
      </div>
    </div>
  </div>
  <section v-if="envProps?.entryPoint === 'bcom'">
    <AtomicUiModalOverlay :id="modalName" width="450px" header="true" @close="closeModal">
      <template #body>
        <ExclusionContent v-if="showOverLay" :exclusion-content="promotion.promotionLegalDisclaimer" />
      </template>
    </AtomicUiModalOverlay>
  </section>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import * as a from '~/utils/UserStatus';
import { useCookie } from '#app';
import ExclusionContent from '~/components/wallet/slideouts/exclusionContent.vue';
import buildCustomerOffersRequestData from '../../utils/customerOffers';
import { useWalletStore } from '~/stores/wallet';
import { useModalOverlayStore } from '@nuxt-common/atomic-ui/app/stores/useModalOverlayStore';
import { useEnvPropsStore } from '~/stores/envProps';
import { useSlideoutStore } from '@nuxt-common/atomic-ui/app/stores/useSlideoutStore';

const props = defineProps({
  promotion: {
    type: Object,
    default() {
      return {};
    },
  },
});

const walletStore = useWalletStore();
const slideoutStore = useSlideoutStore();
const modalOverlayStore = useModalOverlayStore();
const envPropsStore = useEnvPropsStore();
const { toggle: toggleSlideout } = slideoutStore;
const envProps = computed(() => envPropsStore.envProps);
const newOfferSaved = computed(() => walletStore.newOfferSaved);
const customerOffers = computed(() => walletStore.customerOffers);
const isUserLoggedIn = ref(false);
const refreshCustomerOffer = ref(false);
const modalName = ref(`addOfferItem${Math.random().toString(36).slice(2)}`);
const showOverLay = ref(false);

onMounted(() => {
  const result = a.getCookie('MISCGCs');
  isUserLoggedIn.value = (result !== false && result !== '') || false;
});

function openExclusionsDetails() {
  const exclusionContent = props.promotion.promotionLegalDisclaimer;
  walletStore.setSlideout({
    id: 'exclusionDetailsSlideout',
    title: 'Exclusion & Details',
    backable: false,
    component: markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "exclusionContent" */ '../wallet/slideouts/exclusionContent.vue'))),
    props: { exclusionContent },
  });

  toggleSlideout('my-wallet-slideout');
}

function openBcomExclusionsDetails(e) {
  e.preventDefault();

  showOverLay.value = true;
  modalOverlayStore.setModalOverlayVisibility(modalName.value);
}

function checkWalletPromocode() {
  let isPromocodeAdded = false;
  if (customerOffers.value?.walletOffers && customerOffers.value.walletOffers.length) {
    isPromocodeAdded = customerOffers.value.walletOffers.some((offer) => offer.offerCode === props.promotion.promotionCode);
  }
  return isPromocodeAdded;
}

async function addPromoCode() {
  const param = {
    ocd: props.promotion?.promotionCode,
    customerId: useCookie(`${envProps.value?.entryPoint === 'mcom' ? 'macys' : 'bloomingdales'}_online_uid`).value,
  };
  refreshCustomerOffer.value = true;

  await walletStore.addOffer(param);
}

async function getUpdatedCustomerOffers() {
  const dataHeaders = buildCustomerOffersRequestData(envProps.value);
  if (dataHeaders) {
    await walletStore.getCustomerOffers(dataHeaders);
  }
}

function closeModal() {
  showOverLay.value = false;
}

watch(newOfferSaved, (newValue, oldValue) => {
  if (newValue?.offerCode !== oldValue?.offerCode && refreshCustomerOffer.value) {
    refreshCustomerOffer.value = false;
    getUpdatedCustomerOffers();
  }
});
</script>

<style lang="scss" scoped>
.card {
  padding-bottom: rem-calc(10);
}
.card .promotion {
  background-color: $white;
  border: rem-calc(1) solid $gray-4-color;
  width: 100%;
  position: relative;
  padding: rem-calc(15 15 0 15);
  padding-bottom: rem-calc(15);
  min-height: rem-calc(280);
  display: flex;
  display: -ms-flexbox;
  flex: 1;
  -ms-flex: 1 0 auto;
  flex-direction: column;
  -ms-flex-direction: column;
}
.card .promo-header {
  min-height: rem-calc(50);
}
.card .promo-legal p {
  margin-bottom: 0;
  font-size: rem-calc(14);
  line-height: rem-calc(18);
  opacity: 0.9;
}
.card .promo-info {
  width: rem-calc(330.38);
  font-size: rem-calc(14);
  line-height: rem-calc(18);
  padding-left: rem-calc(4);
}
.card .promo-footer {
  margin-top: rem-calc(20);
  width: 100%;
}
@import '~@core/csg/src/scss/encodedIcons/mcom/_offers-filled.scss';

@mixin font-text($size, $line, $spacing) {
  font-style: normal;
  font-weight: normal;
  font-size: rem-calc(16);
  line-height: rem-calc(20);
  letter-spacing: rem-calc(0.2);
}
.disable-add-wallet {
  background-color: $white;
  color: $black;
  opacity: 0.4;
  pointer-events: none;
}
.shop-link {
  color: $white;
  background: $primary-color;
  position: relative;
  margin-bottom: rem-calc(17);
  height: rem-calc(42);
  font-size: rem-calc(14);
  font-weight: 700;
  line-height: rem-calc(17);
  text-align: center;
  border-radius: rem-calc(0);
}
.add-to-button {
  margin-bottom: rem-calc(10);
  height: rem-calc(42);
  font-size: rem-calc(14);
  font-weight: 700;
  line-height: rem-calc(17);
  text-align: center;
  border-radius: rem-calc(0);
  width: 100%;
  border: rem-calc(1) solid $black;
}
.cards-columns-container,
.child_region,
.child_region .promotion {
  height: 100%;
}
</style>
<style lang="scss" brand="mcom" scoped>
.card .promo-desc {
  padding-left: rem-calc(3);
  margin-bottom: rem-calc(10);
  p {
    margin-bottom: 0;
    font-size: rem-calc(14);
    line-height: rem-calc(18);
    opacity: 0.9;
  }
}
.card .promo-header-text {
  color: $black;
  font-size: rem-calc(18);
  line-height: rem-calc(22);
  font-weight: 400;
  padding: rem-calc(3);
}
.card .promo-sub-header-text {
  color: $primary-color;
  font-size: rem-calc(18);
  line-height: rem-calc(22);
  font-weight: 700;
  padding: rem-calc(3);
}
.promotion .promo-body {
  flex: 1;
  -ms-flex: 1 0 auto;
  position: relative;
}
.card .promotion {
  background-color: $white;
  border: rem-calc(1) solid $gray-4-color;
  width: 100%;
  position: relative;
  padding: rem-calc(15 15 0 15);
  padding-bottom: rem-calc(15);
  min-height: rem-calc(280);
  display: flex;
  display: -ms-flexbox;
  flex: 1;
  -ms-flex: 1 0 auto;
  flex-direction: column;
  -ms-flex-direction: column;
}
</style>
<style lang="scss" brand="bcom" scoped>
[id='addOfferItem'] {
  .v-overlay-content {
    padding: 0;
  }
  .v-overlay-header {
    text-align: left;
    border-bottom: 1px solid #d0d0d0;
    padding: 16px;
    letter-spacing: rem-calc(1.067);
  }
  .v-overlay-body {
    margin-top: 0;
    padding: rem-calc(20px 20px);
  }
}
.card .promo-info {
  padding-left: rem-calc(0);
}
.card .promo-desc {
  margin-bottom: rem-calc(10);
  p {
    text-align: left;
    margin-bottom: 0;
    font-size: rem-calc(14);
    opacity: 0.9;
    line-height: 1.57143rem !important;
    font-weight: 400;
  }
}
.LoyallistHeading {
  color: #400099 !important;
}
.info-link {
  color: #767676;
  font-size: rem-calc(12);
  font-weight: 100;
  text-decoration: underline;
}
.card .promo-header-text {
  color: $black;
  font-size: rem-calc(14);
  line-height: rem-calc(22);
  font-weight: 700;
  padding: rem-calc(3);
  padding-bottom: 0%;
}
.card .promo-sub-header-text {
  color: $primary-color;
  font-size: rem-calc(16);
  line-height: rem-calc(22);
  font-weight: 700;
  padding: rem-calc(3);
  padding-top: 0%;
  padding-bottom: rem-calc(10);
  font-style: normal;
  border-bottom: 1px solid #d0d0d0;
}
.promotion .promo-body {
  flex: 1;
  -ms-flex: 1 0 auto;
  position: relative;
  padding-top: rem-calc(20);
}
.card .promotion {
  background-color: $white;
  border: rem-calc(1) solid $gray-4-color;
  width: 100%;
  position: relative;
  padding: rem-calc(18 18 18 18);
  min-height: rem-calc(280);
  display: flex;
  display: -ms-flexbox;
  flex: 1;
  -ms-flex: 1 0 auto;
  flex-direction: column;
  -ms-flex-direction: column;
}
.promo-exp-date {
  padding-bottom: 10px;
  font-weight: 600;
  line-height: 14px;
  text-transform: uppercase;
}
.promo-exp-text {
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.57143rem;
  letter-spacing: 0.07143rem;
}
</style>
<style lang="scss">
div[id^='addOfferItem'] {
  .v-overlay-content {
    padding: rem-calc(32) !important;
  }

  .info-exclusion-text {
    letter-spacing: rem-calc(0.7);
    font-size: rem-calc(12);
  }
}
</style>
